import {
  Alert,
  Button,
  Divider,
  Flex,
  Select,
  Skeleton,
  Typography
} from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { EventGroup } from './hooks'

const { Text } = Typography

type Props = {
  groups: EventGroup[] | undefined
  isLoading: boolean
  isError: boolean
  value?: string[]
  onChange?: (value: string[]) => void
}

/**
 * Grouped, searchable multi-select over CRM's event catalogue.
 *
 * Grouped because 29 events in one flat list is unreadable, and searchable by label as well as by
 * key -- someone debugging a delivery searches for `lead.updated`, someone configuring one searches
 * for "Lead updated".
 */
export const EventSelector = ({
  groups,
  isLoading,
  isError,
  value,
  onChange
}: Props) => {
  const { t } = useTranslation()

  const options = useMemo(
    () =>
      (groups ?? []).map((group) => ({
        label: group.group,
        options: group.events.map((event) => ({
          // Both the label and the key are searchable: the key is what appears in the
          // `X-CRM-Event` header, so an integrator debugging a delivery searches by it.
          label: event.label,
          value: event.key,
          searchText: `${event.label} ${event.key}`.toLowerCase()
        }))
      })),
    [groups]
  )

  const allKeys = useMemo(
    () => (groups ?? []).flatMap((group) => group.events.map((e) => e.key)),
    [groups]
  )

  const selected = value ?? []
  const allSelected =
    allKeys.length > 0 && allKeys.every((key) => selected.includes(key))

  // Replaces the selection rather than merging into it, which is where this differs from
  // AppPermissionsSelect. There, a credential can legitimately hold a code the app owner has since
  // closed, so select-all unions to avoid dropping it. Here an event key absent from the catalogue
  // is simply invalid -- CRM rejects the whole sync with "Unknown events" -- so carrying one
  // forward would break the webhook rather than protect it.
  const selectAll = () => onChange?.(allKeys)

  if (isLoading) return <Skeleton.Input active block size="small" />

  if (isError) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t(
          'workspace.apps.outboundWebhooks.events.unavailable',
          'The event list could not be loaded from this CRM instance.'
        )}
        description={
          <Text type="secondary">
            {t(
              'workspace.apps.outboundWebhooks.events.unavailableHint',
              'The list is served by the CRM app itself. Check that the app is reachable, then reopen this form.'
            )}
          </Text>
        }
      />
    )
  }

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      options={options}
      allowClear
      // Filtering on searchText rather than the label so a key like `lead.created` also matches.
      // Declared as a prop instead of a filterOption callback: AntD types that callback's
      // argument as the group, not the leaf, so a callback cannot read a per-option field.
      optionFilterProp="searchText"
      popupRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: '4px 0' }} />
          <Flex
            justify="space-between"
            align="center"
            style={{ padding: '0 4px 4px' }}
          >
            <Button
              type="link"
              size="small"
              // Keeps focus on the input: AntD closes the dropdown on outside mousedown, which
              // would swallow the click before onChange ran.
              onMouseDown={(e) => e.preventDefault()}
              disabled={allSelected || allKeys.length === 0}
              onClick={selectAll}
            >
              {t(
                'workspace.apps.outboundWebhooks.events.selectAll',
                'Select all'
              )}
            </Button>
            <Button
              type="link"
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              disabled={selected.length === 0}
              onClick={() => onChange?.([])}
            >
              {t('workspace.apps.outboundWebhooks.events.clear', 'Clear')}
            </Button>
          </Flex>
        </>
      )}
      placeholder={t(
        'workspace.apps.outboundWebhooks.events.placeholder',
        'Select the events to receive'
      )}
      maxTagCount="responsive"
    />
  )
}
